import {
  AccountBalanceOutlined,
  BadgeOutlined,
  CheckCircleOutlined,
  DescriptionOutlined,
  MapOutlined,
  PersonOutline,
  SettingsOutlined,
  VerifiedUserOutlined,
} from '@mui/icons-material';
import { CardContainer } from 'finara-components';

import { ErrorState, LoadingState } from '@/components/common';
import { useProcessFlow } from '@/hooks/queries/useOnboarding';

export const ProcessFlowTab: React.FC = () => {
  const { data: processFlowData, isLoading, error } = useProcessFlow();

  const getStepIcon = (stepName: string) => {
    const lowerName = stepName.toLowerCase();

    if (lowerName.includes('bienvenida') || lowerName.includes('welcome')) {
      return <PersonOutline className="text-blue-600" />;
    }
    if (lowerName.includes('datos') || lowerName.includes('personal')) {
      return <BadgeOutlined className="text-green-600" />;
    }
    if (lowerName.includes('documento')) {
      return <DescriptionOutlined className="text-purple-600" />;
    }
    if (lowerName.includes('kyc') || lowerName.includes('verificaci')) {
      return <VerifiedUserOutlined className="text-orange-600" />;
    }
    if (lowerName.includes('banco') || lowerName.includes('cuenta')) {
      return <AccountBalanceOutlined className="text-blue-600" />;
    }
    if (lowerName.includes('configuraci') || lowerName.includes('tarjeta')) {
      return <SettingsOutlined className="text-muted" />;
    }
    if (lowerName.includes('complet') || lowerName.includes('final')) {
      return <CheckCircleOutlined className="text-green-600" />;
    }
    return <PersonOutline className="text-muted" />;
  };

  const getTimelineColor = (index: number): string => {
    const colors = ['text-blue-600', 'text-green-600', 'text-purple-600', 'text-orange-600'];
    return colors[index % colors.length] || 'text-muted';
  };

  if (isLoading) return <LoadingState message="Cargando flujo del proceso..." />;
  if (error)
    return <ErrorState title="Error al cargar el flujo" message="Intenta nuevamente más tarde" />;
  if (!processFlowData) return null;

  return (
    <div className="space-y-6">
      {/* Flujo del Proceso de Onboarding */}
      <CardContainer>
        <div className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <MapOutlined className="text-muted" />
            <h3 className="text-lg font-semibold text-brand-white">Flujo del Proceso de Onboarding</h3>
          </div>

          <div className="space-y-6">
            {processFlowData.steps.map((step, index) => (
              <div key={step.order} className="relative">
                {/* Connector line */}
                {index < processFlowData.steps.length - 1 && (
                  <div className="absolute left-6 top-12 w-0.5 h-12 bg-border"></div>
                )}

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                    {getStepIcon(step.name)}
                  </div>
                  <div className="flex-1 pt-2">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold text-brand-white">{step.name}</h4>
                      <span className="text-sm text-muted">Paso {step.order}</span>
                    </div>
                    <p className="text-sm text-muted">{step.description}</p>

                    {/* Show required documents if available */}
                    {step.requiredDocuments && step.requiredDocuments.length > 0 && (
                      <div className="mt-3 p-3 bg-white/5 rounded-lg">
                        <p className="text-sm font-medium text-muted mb-2">
                          Documentos requeridos:
                        </p>
                        <ul className="text-sm text-muted space-y-1">
                          {step.requiredDocuments.map((doc, idx) => (
                            <li key={idx}>• {doc}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Show verification process if available */}
                    {step.verificationProcess && step.verificationProcess.length > 0 && (
                      <div className="mt-3 p-3 bg-white/5 rounded-lg">
                        <p className="text-sm font-medium text-muted mb-2">
                          Proceso de verificación:
                        </p>
                        <ul className="text-sm text-muted space-y-1">
                          {step.verificationProcess.map((process, idx) => (
                            <li key={idx}>• {process}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContainer>

      {/* Tiempos Promedio por Paso */}
      <CardContainer>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-brand-white mb-6">Tiempos Promedio por Paso</h3>

          <div className="grid grid-cols-1 md:!grid-cols-4 gap-6">
            {processFlowData.steps.slice(0, 4).map((step, index) => (
              <div key={step.order} className="text-center">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className={`text-3xl font-bold ${getTimelineColor(index)}`}>
                    {step.averageTime}
                  </span>
                </div>
                <p className={`text-sm font-semibold ${getTimelineColor(index)} mb-1`}>
                  {step.averageTime} {step.averageTimeUnit}
                </p>
                <p className="text-sm text-muted">{step.name}</p>
              </div>
            ))}
          </div>

          {/* Total Average Time */}
          <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
            <p className="text-sm text-muted mb-1">Tiempo promedio total del proceso</p>
            <p className="text-2xl font-bold text-brand-blue">
              {processFlowData.totalAverageTime} {processFlowData.totalAverageTimeUnit}
            </p>
          </div>
        </div>
      </CardContainer>
    </div>
  );
};
